import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../../redux";
import PopoverHelp from "../../../../Docuclipper/PopoverHelp";
import CsvOptions from "./CsvOptions";
import ExportTypeDropdown from "./ExportTypeDropdown";
import FieldNamesDropdown from "./FieldNamesDropdown";
import GenericOptions from "./GenericOptions";
import IifOptions from "./IifOptions";
import Instructions from "./Instructions";
import QboOptions from "./QboOptions";
import * as Yup from "yup";
import { Field, Formik } from "formik";
import { formikInput, sendGAEvent } from "../../../../../utils/utils";
import { downloadSpreadsheetHelper } from "./utils";
import { automaticModeAsync } from "../../../../../redux/reducers/AutomaticModeAsync";
import ClipLoader from "react-spinners/ClipLoader";
import Select from "react-select";
import { getDocumentName } from "src/views/Pages/Analyze/AnalyzeTransactionsTable/utils";
import { setDownloadOptionsSelectedDocumentIds } from "../../../../../redux/reducers/TransactionManager";
import { setDownloadTemplateModalOpen } from "../../../../../redux/reducers/JobExport";
import DocumentsDropdown from "./DocumentsDropdown";

// Validation schema
const validationSchema = Yup.object({
  action: Yup.string(),
});

const PreviewForm = () => {
  const dispatch = useDispatch();
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { tls } = useSelector((state: ReduxState) => state.TransactionManager);
  const { loading: asyncLoading } = useSelector(
    (state: ReduxState) => state.AutomaticModeAsync
  );
  const { selectedDocumentId, downloadOptions } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { selectedDocumentIds } = downloadOptions;

  const { documents } = tls;

  const [loading, setLoading] = React.useState(false);
  const showModal = useSelector(
    (state: ReduxState) => state.JobExport.downloadTemplateModalOpen
  );
  const setShowModal = (v: boolean) =>
    dispatch(setDownloadTemplateModalOpen(v));
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );

  const uniqueDocumentIds = Array.from(
    new Set((jobDocs || []).map((item) => item.documentId))
  );
  const options = uniqueDocumentIds.map((docId) => ({
    value: docId,
    label: getDocumentName(jobDocs || [], docId),
  }));

  // Parse query parameters for new documents
  const queryParams = new URLSearchParams(window.location.search);
  const newDocumentsParam = queryParams.get("newDocuments");

  // Use a stable id-set key so this effect re-runs when the user navigates between jobs that
  // happen to have the same document count (length alone missed that case and let stale
  // selectedDocumentIds from a prior job leak into the next export POST).
  const jobDocsKey = uniqueDocumentIds.map(String).sort().join(",");

  useEffect(() => {
    if ((jobDocs || []).length === 0) return;

    if (selectedDocumentIds.length === 0) {
      if (newDocumentsParam) {
        const newDocs = newDocumentsParam.split(",");
        dispatch(setDownloadOptionsSelectedDocumentIds(newDocs));
      } else {
        dispatch(setDownloadOptionsSelectedDocumentIds(uniqueDocumentIds));
      }
      return;
    }

    // Prune anything that doesn't belong to the current job — defense against stale Redux state
    // when the user switches jobs without closing the app.
    const validSet = new Set(uniqueDocumentIds.map(String));
    const pruned = selectedDocumentIds.filter((id) => validSet.has(String(id)));
    if (pruned.length === 0) {
      dispatch(setDownloadOptionsSelectedDocumentIds(uniqueDocumentIds));
    } else if (pruned.length !== selectedDocumentIds.length) {
      dispatch(setDownloadOptionsSelectedDocumentIds(pruned));
    }
  }, [selectedDocumentIds.length, jobDocsKey, newDocumentsParam]);

  const selectedOptions = options.filter((option) =>
    selectedDocumentIds.includes(option.value)
  );

  const handleSelectAll = () => {
    // Select all documents without any filtering
    dispatch(setDownloadOptionsSelectedDocumentIds(uniqueDocumentIds));
  };

  const handleSelectLatest = () => {
    if (newDocumentsParam) {
      // Select documents from the newDocuments query parameter
      const newDocs = newDocumentsParam.split(",");
      dispatch(setDownloadOptionsSelectedDocumentIds(newDocs));
    }
  };

  const handleDownload = (selectedDocumentIds: string[]) => {
    sendGAEvent({
      action: "exportDownloadClick",
      category: "export",
      label: "template",
    });
    const T = 15;
    const T2 = 50;
    const T3 = 10;
    const T4 = 50;

    setLoading(true);
    downloadSpreadsheetHelper(
      job,
      l1,
      l2,
      "FieldsToExcel1",
      "xlsx",
      {
        T,
        T2,
        T3,
        T4,
        separateFilesForAccounts: false,
        fileType: "csv",
        splitMultilineDescription: false,
      },
      selectedDocumentIds,
      ["template"],
      [],
      [],
      "date",
      "asc",
      "CSV"
    )
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  return (
    <div>
      {(loading || asyncLoading) && <ClipLoader />}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header
          closeButton
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
          placeholder={undefined}
        >
          <Modal.Title>Download</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div className="font-weight-bold">Documents</div>
            <DocumentsDropdown />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const selectedIds =
                selectedDocumentIds.length > 0
                  ? selectedDocumentIds.filter(
                      (id): id is string => id !== null
                    )
                  : selectedDocumentId
                  ? [selectedDocumentId]
                  : [];
              handleDownload(selectedIds);
              setShowModal(false);
            }}
          >
            Download
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

type Props = {};
export const DownloadTemplate: React.FC<Props> = ({}) => {
  const { options, selectedExportType } = useSelector(
    (state: ReduxState) => state.DownloadOptions
  );
  const { job } = useSelector((state: ReduxState) => state.JobData);

  const [loading, setLoading] = React.useState(false);

  return (
    <Row className="align-items-center">
      <PreviewForm />
    </Row>
  );
};
export default DownloadTemplate;
